import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import useDesks from '@/hooks/useDesks';
import { getComponentsFromTemplate } from '@/modules/whatsapp/utils';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import {
  CampaignSender,
  CampaignSenderStatus,
  EmailTemplate,
  SmsTemplate,
} from '@/shared/types/campaigns';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { getChannelDisplayName } from '@/util/channelName';
import { getConfigKey } from '@/util/outboundIntegrations';

import { getPhoneFromIntegrationName } from '../../utils';

interface Props {
  senders: CampaignSender[];
  isLoading?: boolean;
}

/**
 * Extracts the human-readable message body from a persisted sender, mirroring
 * the content resolution in useCampaignBuildFormInit.
 */
const getSenderMessageBody = (sender: CampaignSender): string => {
  const { template, integration_type: type } = sender;
  if (!template) {
    return '';
  }
  if (type === IntegrationType.EMAIL) {
    return (template as unknown as EmailTemplate).body ?? '';
  }
  if (type === IntegrationType.WHATSAPP) {
    return getComponentsFromTemplate(template as unknown as WhatsappTemplate).body?.text ?? '';
  }
  return (template as unknown as SmsTemplate).text ?? '';
};

/**
 * Resolves the sender identifier shown under the channel name — the outbound
 * phone number (WhatsApp/SMS/Voice), sender name (RCS) or from-address (Email).
 * Reads the structured `config` value used at campaign build time, falling back
 * to parsing a number from the integration name, then the name itself.
 */
const getSenderIdentifier = (sender: CampaignSender, integration?: Integration): string => {
  const type = sender.integration_type as IntegrationType;
  const configKey = getConfigKey(type);
  const config = integration?.config as Record<string, unknown> | undefined;
  const rawValue = configKey && config ? config[configKey] : undefined;

  if (typeof rawValue === 'string' && rawValue.trim()) {
    // Email/RCS identifiers are shown verbatim; phone-based channels get a leading '+'.
    if (type === IntegrationType.EMAIL || type === IntegrationType.INFOBIP_RCS) {
      return rawValue;
    }
    return rawValue.startsWith('+') ? rawValue : `+${rawValue}`;
  }

  return getPhoneFromIntegrationName(integration?.name) ?? integration?.name ?? '';
};

/**
 * "Initial messages" section: the message body sent on each channel of a
 * campaign, grouped by sender. Hidden when the campaign has no senders.
 */
export const CampaignInitialMessages = ({ senders, isLoading }: Props) => {
  const { t } = useTranslation();
  const { desks, getAllIntegrations } = useDesks();
  const [integrationMap, setIntegrationMap] = useState<Map<string, Integration>>(new Map());

  // Only show user-authored messages. NBA follow-ups are system-generated
  // (flagged via metadata.nba_followup) and must be excluded.
  const visibleSenders = useMemo(() => {
    const authored = senders.filter(
      (s) => !(s.metadata as { nba_followup?: boolean } | undefined)?.nba_followup
    );
    // Prefer active senders; fall back to all authored senders when none are flagged active.
    const active = authored.filter((s) => s.status === CampaignSenderStatus.ACTIVE);
    return active.length > 0 ? active : authored;
  }, [senders]);

  const senderTypes = useMemo(
    () => [...new Set(visibleSenders.map((s) => s.integration_type).filter(Boolean))],
    [visibleSenders]
  );

  useEffect(() => {
    let cancelled = false;
    if (!desks || senderTypes.length === 0) {
      return;
    }
    (async () => {
      const lists = await Promise.all(
        senderTypes.map((type) => getAllIntegrations(type as IntegrationType))
      );
      if (cancelled) {
        return;
      }
      const map = new Map<string, Integration>();
      lists.flat().forEach((integration) => {
        if (integration.integration_id) {
          map.set(integration.integration_id, integration);
        }
      });
      setIntegrationMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [desks, senderTypes, getAllIntegrations]);

  if (isLoading) {
    return (
      <section className="mt-8 px-6">
        <h2 className="mb-6 text-lg leading-none font-semibold text-foreground">
          {t('campaigns.analytics.initial_messages')}
        </h2>
        <div className="space-y-6">
          {[0, 1].map((row) => (
            <div key={row} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
              <div className="w-40 shrink-0 space-y-1">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (visibleSenders.length === 0) {
    return null;
  }

  return (
    <section className="mt-8 px-6">
      <h2 className="mb-6 text-lg leading-none font-semibold text-foreground">
        {t('campaigns.analytics.initial_messages')}
      </h2>
      <div className="space-y-6">
        {visibleSenders.map((sender) => {
          const integration = sender.integration_id
            ? integrationMap.get(sender.integration_id)
            : undefined;
          const identifier = getSenderIdentifier(sender, integration);
          const body = getSenderMessageBody(sender);

          return (
            <div key={sender.sender_id} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
              <div className="w-40 shrink-0">
                <p className="text-sm leading-none font-medium text-foreground">
                  {getChannelDisplayName(sender.integration_type as IntegrationType)}
                </p>
                {identifier && (
                  <p className="mt-1 truncate text-sm text-foreground-muted">{identifier}</p>
                )}
              </div>
              <p className="flex-1 text-sm leading-6 font-medium whitespace-pre-line text-foreground">
                {body || '—'}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};
